import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { Title, useNotify, useTranslate } from "react-admin";

import { RequirePermission } from "../../components/RequirePermission";
import { ResourceName } from "../../constants/resources";
import {
  CheckPlacetelConnectionDocument,
  PlacetelCallHistoryAccess,
  PlacetelConnectionError,
  PlacetelIntegrationDocument,
  PlacetelPhoneAccountDocument,
  UpdatePlacetelIntegrationDocument,
} from "../../generated/graphql";
import { captureException } from "../../lib/errorHandling";
import { placetelIntegrationErrorKey } from "../../lib/placetelIntegration";
import { phoneSignInRedirectUri } from "../../lib/placetelPhoneSignIn";
import { HotlineNumbersSettings } from "./HotlineNumbersSettings";
import { OpeningHoursSettings } from "./OpeningHoursSettings";

type FormValues = {
  enabled: boolean;
  organizationId: string;
  clientId: string;
  clientSecret: string;
  refreshToken: string;
  webhookSecret: string;
  phoneClientId: string;
  phoneClientSecret: string;
};

const emptySecret = (value: string): string | undefined => value || undefined;

const connectionErrorKeys: Record<PlacetelConnectionError, string> = {
  [PlacetelConnectionError.NotConfigured]: "integrations.placetel.connectionErrors.NOT_CONFIGURED",
  [PlacetelConnectionError.CredentialsUnavailable]: "integrations.placetel.connectionErrors.CREDENTIALS_UNAVAILABLE",
  [PlacetelConnectionError.AuthenticationFailed]: "integrations.placetel.connectionErrors.AUTHENTICATION_FAILED",
  [PlacetelConnectionError.InsufficientScope]: "integrations.placetel.connectionErrors.INSUFFICIENT_SCOPE",
  [PlacetelConnectionError.OrganizationNotFound]: "integrations.placetel.connectionErrors.ORGANIZATION_NOT_FOUND",
  [PlacetelConnectionError.OrganizationMismatch]: "integrations.placetel.connectionErrors.ORGANIZATION_MISMATCH",
  [PlacetelConnectionError.InvalidResponse]: "integrations.placetel.connectionErrors.INVALID_RESPONSE",
  [PlacetelConnectionError.Timeout]: "integrations.placetel.connectionErrors.TIMEOUT",
  [PlacetelConnectionError.RateLimited]: "integrations.placetel.connectionErrors.RATE_LIMITED",
  [PlacetelConnectionError.ProviderError]: "integrations.placetel.connectionErrors.PROVIDER_ERROR",
  [PlacetelConnectionError.NetworkError]: "integrations.placetel.connectionErrors.NETWORK_ERROR",
};

const callHistoryAccessKeys: Record<PlacetelCallHistoryAccess, string> = {
  [PlacetelCallHistoryAccess.Ok]: "integrations.placetel.callHistory.OK",
  [PlacetelCallHistoryAccess.Forbidden]: "integrations.placetel.callHistory.FORBIDDEN",
  [PlacetelCallHistoryAccess.RateLimited]: "integrations.placetel.callHistory.RATE_LIMITED",
  [PlacetelCallHistoryAccess.Failed]: "integrations.placetel.callHistory.FAILED",
};

type ConnectionFeedback =
  | { accountName: string; callHistory: PlacetelCallHistoryAccess | null; kind: "success" }
  | { errorCode: PlacetelConnectionError | null; kind: "error" };

const PlacetelIntegrationSettings = () => {
  const client = useApolloClient();
  const translate = useTranslate();
  const notify = useNotify();
  const { data, loading, error } = useQuery(PlacetelIntegrationDocument, {
    fetchPolicy: "network-only",
  });
  const [form, setForm] = useState<FormValues>({
    enabled: false,
    organizationId: "",
    clientId: "",
    clientSecret: "",
    refreshToken: "",
    webhookSecret: "",
    phoneClientId: "",
    phoneClientSecret: "",
  });
  const [initialized, setInitialized] = useState(false);
  const [secretPresence, setSecretPresence] = useState({
    hasClientSecret: false,
    hasRefreshToken: false,
    hasWebhookSecret: false,
    hasPhoneClientSecret: false,
  });
  const [connectionFeedback, setConnectionFeedback] = useState<ConnectionFeedback | null>(null);
  const saveInFlight = useRef(false);
  const testInFlight = useRef(false);
  const [updatePlacetelIntegration, { loading: saving }] = useMutation(UpdatePlacetelIntegrationDocument, {
    update: (cache, { data: mutationData }) => {
      const settings = mutationData?.updatePlacetelIntegration;
      if (!settings) return;
      cache.writeQuery({
        query: PlacetelIntegrationDocument,
        data: { placetelIntegration: settings },
      });
    },
  });
  const [checkPlacetelConnection, { loading: checking }] = useMutation(CheckPlacetelConnectionDocument);

  const settings = data?.placetelIntegration;

  useEffect(() => {
    if (loading || error || !settings || initialized) return;
    setForm({
      enabled: settings.enabled,
      organizationId: settings.organizationId ?? "",
      clientId: settings.clientId ?? "",
      clientSecret: "",
      refreshToken: "",
      webhookSecret: "",
      phoneClientId: settings.phoneClientId ?? "",
      phoneClientSecret: "",
    });
    setSecretPresence({
      hasClientSecret: settings.hasClientSecret,
      hasRefreshToken: settings.hasRefreshToken,
      hasWebhookSecret: settings.hasWebhookSecret,
      hasPhoneClientSecret: settings.hasPhoneClientSecret,
    });
    setInitialized(true);
  }, [error, initialized, loading, settings]);

  const secretHelper = (hasStoredSecret: boolean): string =>
    translate(hasStoredSecret ? "integrations.placetel.secretStored" : "integrations.placetel.secretRequired");

  // Once a configuration is saved, a changed organization or client ID needs the
  // client secret and refresh token entered again.
  const savedOrganizationId = settings?.organizationId;
  const connectionIdentityChanged =
    Boolean(savedOrganizationId) &&
    (form.organizationId.trim() !== savedOrganizationId || form.clientId.trim() !== (settings?.clientId ?? ""));
  const connectionSecretHelper = (hasStoredSecret: boolean): string =>
    connectionIdentityChanged ? translate("integrations.placetel.secretRequiredAgain") : secretHelper(hasStoredSecret);

  // The phone sign-in app is independent of the Service App; a changed client ID
  // needs its secret entered again, and an empty one needs no secret.
  const phoneClientIdChanged =
    Boolean(settings?.phoneClientId) && form.phoneClientId.trim() !== (settings?.phoneClientId ?? "");
  const phoneSecretHelper = (): string | undefined => {
    if (form.phoneClientId.trim() === "") return undefined;
    if (phoneClientIdChanged) return translate("integrations.placetel.phoneSecretRequiredAgain");
    return secretHelper(secretPresence.hasPhoneClientSecret);
  };

  const hasUnsavedChanges =
    form.enabled !== settings?.enabled ||
    form.organizationId !== (settings?.organizationId ?? "") ||
    form.clientId !== (settings?.clientId ?? "") ||
    form.clientSecret.length > 0 ||
    form.refreshToken.length > 0 ||
    form.webhookSecret.length > 0 ||
    form.phoneClientId !== (settings?.phoneClientId ?? "") ||
    form.phoneClientSecret.length > 0;
  const hasSavedConnectionSettings = Boolean(
    settings?.organizationId && settings.clientId && secretPresence.hasClientSecret && secretPresence.hasRefreshToken
  );
  const formDisabled = saving || checking;
  const canTestConnection =
    initialized &&
    hasSavedConnectionSettings &&
    !hasUnsavedChanges &&
    !formDisabled &&
    !saveInFlight.current &&
    !testInFlight.current;

  const updateForm = (change: Partial<FormValues>) => {
    setConnectionFeedback(null);
    setForm(current => ({ ...current, ...change }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!initialized || saving || checking || saveInFlight.current || testInFlight.current) {
      return;
    }

    setConnectionFeedback(null);
    saveInFlight.current = true;
    try {
      const result = await updatePlacetelIntegration({
        variables: {
          input: {
            enabled: form.enabled,
            organizationId: form.organizationId,
            clientId: form.clientId,
            clientSecret: emptySecret(form.clientSecret),
            refreshToken: emptySecret(form.refreshToken),
            webhookSecret: emptySecret(form.webhookSecret),
            phoneClientId: form.phoneClientId,
            phoneClientSecret: emptySecret(form.phoneClientSecret),
          },
        },
      });
      const saved = result.data?.updatePlacetelIntegration;
      if (!saved) throw new Error();
      void client.refetchQueries({ include: [PlacetelPhoneAccountDocument] }).catch(refetchError => {
        captureException(refetchError, "placetel/integration: refresh phone account");
      });

      setForm({
        enabled: saved.enabled,
        organizationId: saved.organizationId ?? "",
        clientId: saved.clientId ?? "",
        clientSecret: "",
        refreshToken: "",
        webhookSecret: "",
        phoneClientId: saved.phoneClientId ?? "",
        phoneClientSecret: "",
      });
      setSecretPresence({
        hasClientSecret: saved.hasClientSecret,
        hasRefreshToken: saved.hasRefreshToken,
        hasWebhookSecret: saved.hasWebhookSecret,
        hasPhoneClientSecret: saved.hasPhoneClientSecret,
      });
      notify("integrations.saved", { type: "success" });
    } catch (saveError) {
      const key = placetelIntegrationErrorKey(saveError);
      if (key === null) captureException(saveError, "placetel/integration: save settings");
      notify(translate(key ?? "integrations.saveError"), { type: "error" });
    } finally {
      saveInFlight.current = false;
    }
  };

  const handleConnectionCheck = async () => {
    if (!canTestConnection || saveInFlight.current || testInFlight.current) {
      return;
    }

    setConnectionFeedback(null);
    testInFlight.current = true;
    try {
      const result = await checkPlacetelConnection();
      const connection = result.data?.checkPlacetelConnection;
      if (connection?.ok && connection.accountName) {
        setConnectionFeedback({
          kind: "success",
          accountName: connection.accountName,
          callHistory: connection.callHistory ?? null,
        });
      } else {
        setConnectionFeedback({
          kind: "error",
          errorCode: connection?.errorCode ?? null,
        });
      }
    } catch {
      setConnectionFeedback({ kind: "error", errorCode: null });
    } finally {
      testInFlight.current = false;
    }
  };

  const connectionFeedbackMessage =
    connectionFeedback?.kind === "success"
      ? translate("integrations.placetel.connectionSuccess", {
          accountName: connectionFeedback.accountName,
        })
      : connectionFeedback?.errorCode
        ? translate(connectionErrorKeys[connectionFeedback.errorCode])
        : translate("integrations.placetel.connectionError");

  if (error) return <Alert severity="error">{translate("integrations.loadError")}</Alert>;
  if (loading || !settings || !initialized) {
    return <CircularProgress aria-label={translate("integrations.loading")} />;
  }

  return (
    <Card>
      <CardContent>
        <Typography component="h2" variant="h6" gutterBottom>
          {translate("integrations.placetel.title")}
        </Typography>
        <Typography color="text.secondary" paragraph>
          {translate("integrations.placetel.description")}
        </Typography>
        <Typography color="text.secondary" paragraph>
          {translate("integrations.placetel.credentialsHint")}
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <FormControlLabel
            control={
              <Switch
                checked={form.enabled}
                disabled={formDisabled}
                onChange={event => updateForm({ enabled: event.target.checked })}
              />
            }
            label={translate("integrations.placetel.enabled")}
          />
          <TextField
            fullWidth
            disabled={formDisabled}
            label={translate("integrations.placetel.organizationId")}
            margin="normal"
            onChange={event => updateForm({ organizationId: event.target.value })}
            value={form.organizationId}
          />
          <TextField
            fullWidth
            disabled={formDisabled}
            label={translate("integrations.placetel.clientId")}
            margin="normal"
            onChange={event => updateForm({ clientId: event.target.value })}
            value={form.clientId}
          />
          <TextField
            autoComplete="new-password"
            disabled={formDisabled}
            fullWidth
            helperText={connectionSecretHelper(secretPresence.hasClientSecret)}
            label={translate("integrations.placetel.clientSecret")}
            margin="normal"
            onChange={event => updateForm({ clientSecret: event.target.value })}
            type="password"
            value={form.clientSecret}
          />
          <TextField
            autoComplete="new-password"
            disabled={formDisabled}
            fullWidth
            helperText={connectionSecretHelper(secretPresence.hasRefreshToken)}
            label={translate("integrations.placetel.refreshToken")}
            margin="normal"
            onChange={event => updateForm({ refreshToken: event.target.value })}
            type="password"
            value={form.refreshToken}
          />
          <TextField
            autoComplete="new-password"
            disabled={formDisabled}
            fullWidth
            helperText={secretHelper(secretPresence.hasWebhookSecret)}
            label={translate("integrations.placetel.webhookSecret")}
            margin="normal"
            onChange={event => updateForm({ webhookSecret: event.target.value })}
            type="password"
            value={form.webhookSecret}
          />
          <Typography component="h3" variant="subtitle1" sx={{ mt: 3 }}>
            {translate("integrations.placetel.phoneTitle")}
          </Typography>
          <Typography color="text.secondary" paragraph>
            {translate("integrations.placetel.phoneDescription")}
          </Typography>
          <Typography color="text.secondary" paragraph>
            {translate("integrations.placetel.phoneRedirectUri", {
              redirectUri: phoneSignInRedirectUri(),
            })}
          </Typography>
          <TextField
            fullWidth
            disabled={formDisabled}
            helperText={translate("integrations.placetel.phoneClientIdHelp")}
            label={translate("integrations.placetel.phoneClientId")}
            margin="normal"
            onChange={event => updateForm({ phoneClientId: event.target.value })}
            value={form.phoneClientId}
          />
          <TextField
            autoComplete="new-password"
            disabled={formDisabled}
            fullWidth
            helperText={phoneSecretHelper()}
            label={translate("integrations.placetel.phoneClientSecret")}
            margin="normal"
            onChange={event => updateForm({ phoneClientSecret: event.target.value })}
            type="password"
            value={form.phoneClientSecret}
          />
          {canTestConnection ? (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              {translate("integrations.placetel.connectionUsesSavedValues")}
            </Typography>
          ) : !saving && !checking ? (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              {translate("integrations.placetel.connectionSaveFirst")}
            </Typography>
          ) : null}
          {connectionFeedback ? (
            <Alert severity={connectionFeedback.kind === "success" ? "success" : "error"} sx={{ mt: 2 }}>
              {connectionFeedbackMessage}
              {connectionFeedback.kind === "success" && connectionFeedback.callHistory ? (
                <Typography component="span" display="block" variant="body2">
                  {translate(callHistoryAccessKeys[connectionFeedback.callHistory])}
                </Typography>
              ) : null}
            </Alert>
          ) : null}
          <Button disabled={formDisabled} sx={{ mt: 2 }} type="submit" variant="contained">
            {translate(saving ? "integrations.placetel.saving" : "integrations.placetel.save")}
          </Button>
          <Button
            disabled={!canTestConnection}
            onClick={handleConnectionCheck}
            sx={{ mt: 2, ml: 1 }}
            type="button"
            variant="outlined"
          >
            {translate(checking ? "integrations.placetel.checkingConnection" : "integrations.placetel.checkConnection")}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export const CustomerServiceIntegrationsPage = () => {
  const translate = useTranslate();

  return (
    <RequirePermission permission={ResourceName.Integrations}>
      <Box sx={{ maxWidth: 720, margin: "auto", mt: 3 }}>
        <Title title={translate("integrations.title")} />
        <Typography component="h1" variant="h4" gutterBottom>
          {translate("integrations.title")}
        </Typography>
        <PlacetelIntegrationSettings />
        <Box sx={{ mt: 3 }}>
          <OpeningHoursSettings />
        </Box>
        <Box sx={{ mt: 3 }}>
          <HotlineNumbersSettings />
        </Box>
      </Box>
    </RequirePermission>
  );
};
